import React, { FC } from 'react';
import { Box, Typography, TextField, FormControl, Select, InputLabel, MenuItem, Button } from '@mui/material';

import type { RaceLogEntry } from '../../models/race.models';
import { palette } from '../../theme';
import { parseRaceDateTime } from '../../helpers/general.helpers';

const EQUIPMENT_PERFORMANCE_LABELS: Record<number, string> = {
  1: 'Bad',
  2: 'Okay',
  3: 'Good',
  4: 'Great',
  5: 'Excellent',
};

function getPerformanceLabel(value: number): string {
  return EQUIPMENT_PERFORMANCE_LABELS[value] ?? String(value);
}

const PERFORMANCE_OPTIONS = [1, 2, 3, 4, 5].map((value) => ({
  value,
  label: getPerformanceLabel(value),
}));

export type LinkRaceLogEntryFormFields = 'race_type' | 'result' | 'equipment_performance' | 'race_note';

export interface LinkRaceLogEntryFormForm {
  race_type: string;
  result: number | '';
  equipment_performance: number | '';
  race_note: string;
}

interface LinkRaceLogEntryFormProps {
  race: RaceLogEntry;
  form: LinkRaceLogEntryFormForm;
  onFormChange: (field: LinkRaceLogEntryFormFields, value: string | number) => void;
  onAddEntry?: () => void;
  isSubmitting?: boolean;
}

const LinkRaceLogEntryForm: FC<LinkRaceLogEntryFormProps> = ({
  race,
  form,
  onFormChange,
  onAddEntry,
  isSubmitting = false,
}) => {
  const { dateDisplay, timeDisplay } = parseRaceDateTime(race.race_date);

  return (
    <Box
      sx={{
        marginBottom: 2,
        padding: 1,
        backgroundColor: palette.background.subtle,
        borderRadius: 1,
        border: `1px solid ${palette.grey[200]}`,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr',
        gap: 2,
        alignItems: 'flex-start',
      }}
    >
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <Typography variant="subtitle2" sx={{ textAlign: 'center' }}>
          {race.race_name}
        </Typography>
        <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
          Event
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <Typography variant="subtitle2" sx={{ width: '100%', textAlign: 'center' }}>
          {race.race_type}
        </Typography>
        <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
          Race
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <Typography variant="subtitle2">{dateDisplay}</Typography>
        <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
          Date
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <Typography variant="subtitle2">{timeDisplay}</Typography>
        <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
          Time of Day
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <Typography variant="subtitle2">{race.race_location}</Typography>
        <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
          Location
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <Typography variant="subtitle2">{race.result}</Typography>
        <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
          Result
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <FormControl size="small" variant="outlined" sx={{ width: '100%' }}>
          <InputLabel id="equipment-performance-label">Ski Performance</InputLabel>
          <Select
            labelId="equipment-performance-label"
            label="Ski Performance"
            value={form.equipment_performance === '' ? '' : form.equipment_performance}
            onChange={(e) => onFormChange('equipment_performance', e.target.value === '' ? '' : Number(e.target.value))}
          >
            <MenuItem value="">
              <em>Select</em>
            </MenuItem>
            {PERFORMANCE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
          Ski Performance
        </Typography>
      </Box>
      <Box
        sx={{
          minWidth: 0,
          gridColumn: '2 / -1',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <TextField
          size="small"
          multiline
          minRows={3}
          placeholder="Ski performance notes..."
          value={form.race_note}
          onChange={(e) => onFormChange('race_note', e.target.value)}
          variant="outlined"
          sx={{ width: '100%' }}
        />
      </Box>
      <Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
        <Button variant="contained" type="button" onClick={() => onAddEntry?.()} disabled={isSubmitting}>
          Link Entry
        </Button>
      </Box>
    </Box>
  );
};

export default LinkRaceLogEntryForm;
